/* eslint-disable @typescript-eslint/no-use-before-define */
import { type ReactNode } from 'react';
import { useMemo } from 'react';
import * as React from 'react';
import { mix } from 'polished';
import styled, { css } from 'styled-components';

import { Button, Heading, Text } from '../design-system';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const StyledHeading = styled(Heading)<{ $withGradientHeading?: boolean }>`
  margin-bottom: 10px;

  span {
    display: block;

    @media (max-width: ${({ theme }) => theme.mediaQuery.SMALL}) {
      display: inline;
    }
  }

  ${({ $withGradientHeading, theme }) => {
    if (!$withGradientHeading) return '';

    const stop1 = theme.fontColor.LOUD;
    const stop2 = mix(0.6, theme.highlightColor, theme.fontColor.LOUD);
    return css`
      background-size: cover;
      background-image: ${`linear-gradient(to right, ${stop1}, ${stop2})`};
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      -moz-background-clip: text;
      -moz-text-fill-color: transparent;
    `;
  }}
`;

const Icon = styled.div`
  margin-bottom: 18px;

  > div {
    width: 30px;
    height: 30px;

    > svg {
      width: 100%;
      height: 100%;
      color: ${({ theme }) => theme.colors.LIGHT_PURPLE};
    }
  }
`;

interface InfoTextProps {
  ctaText?: string;
  description?: ReactNode | ReactNode[];
  descriptionNode?: ReactNode;
  icon?: ReactNode;
  link?: string;
  smallTitle?: ReactNode;
  title: ReactNode;
  withGradientHeading?: boolean;
}

export const InfoText: React.ComponentType<InfoTextProps> = React.memo(
  ({
    title,
    description,
    descriptionNode,
    link,
    icon,
    ctaText,
    smallTitle,
    withGradientHeading
  }) => {
    const descriptionRender = useMemo(() => {
      if (descriptionNode) {
        return descriptionNode;
      }
      return Array.isArray(description) ? (
        <>
          {description.map((line, i) => (
            <Text
              key={i}
              css={`
                margin-bottom: 20px;
              `}
              size="large">
              {line}
            </Text>
          ))}
        </>
      ) : (
        <Text
          css={`
            margin-bottom: 20px;
          `}
          size="large">
          {description}
        </Text>
      );
    }, [description, descriptionNode]);

    return (
      <Wrapper className="info-text">
        {smallTitle && (
          <StyledHeading renderAs="h6">{smallTitle}</StyledHeading>
        )}
        {icon && <Icon>{icon}</Icon>}
        <StyledHeading $withGradientHeading={withGradientHeading} renderAs="h2">
          {title}
        </StyledHeading>
        {descriptionRender}
        {link && <Button link={link} text={ctaText || 'Learn more'} />}
      </Wrapper>
    );
  }
);
